import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';

import { InventoryLocation } from './inventory-location';
import { LocationsHeader } from './locations-header';
import { ModalPortal } from '../../../../../../components/modal-portal';
import { useModal } from '../../../../../../hooks/use-modal';
import { usePusherEvent } from '../../../../../../hooks/use-pusher-event';
import { INVENTORY_ACTION_COMPLETE } from '../../../../../../services/pusher-service/pusher-contract-messages';
import Global from '../../../../../../store/global-selectors';
import { useProductDetailsContext } from '../../../../use-product-details-context';
import { messages } from '../messages';
import { AddStockModal } from '../modals/add-alternate-location';
import { MoveInventoryModal } from '../modals/move-inventory';
import style from '../style.module.css';

type InventoryWarehouse = {
  isActive: boolean;
  createDate: string;
  externalInventoryWarehouseGuid: string;
  inventoryWarehouseId: string;
  linkToDefault: boolean;
  locationDimensions: number;
  modifyDate: string;
  name: string;
};

type WarehouseLocation = {
  inventoryWarehouseId: string;
  inventoryLocationId: string;
  area: string;
  unit: string;
  shelf: string;
  bin: string;
};

type MoveInventoryParams = {
  inventoryLocationId: string;
  lotUuid: string;
};

type InventoryLocationItem = {
  allocated: number;
  available: number;
  expirationDate: string | null;
  inventoryLocationId: string;
  location: string;
  lot: string;
  lotUuid: string;
  stock: number;
  warehouseName: string;
};

const NoLocationsMade = () => (
  <div className={style['no-locations']}>
    <Text textType={TextType.Paragraph} value={messages.noLocations} />
  </div>
);

export const LocationsGridV3 = () => {
  const {
    inventoryWarehouses,
    inventoryWarehouseLocations,
    isActive,
    productId,
    productName,
    productSku,
    inventoryLocations,
    isLoadingInventoryLocations,
    setShouldLoadInventoryLocations,
    productWarehouseLocation,
  } = useProductDetailsContext();

  const onReloadLocations = useCallback(() => {
    setShouldLoadInventoryLocations(true);
  }, [setShouldLoadInventoryLocations]);

  const currentUserId = useSelector(Global.selectUserCurrentUserId);

  const onReloadLocationsAfterPusherMessage = useCallback(
    (pusherMessage: { UserID: number; Success: boolean }) => {
      if (pusherMessage && pusherMessage.UserID === currentUserId && pusherMessage.Success) {
        setShouldLoadInventoryLocations(true);
      }
    },
    [setShouldLoadInventoryLocations, currentUserId],
  );

  usePusherEvent(INVENTORY_ACTION_COMPLETE, onReloadLocationsAfterPusherMessage);

  const { open: openAddInventoryModal, close: closeAddInventoryModal, isOpen: isAddInventoryModalOpen } = useModal();
  const { open: openMoveInventoryModal, close: closeMoveInventoryModal, isOpen: isMoveInventoryModalOpen } = useModal();
  const [moveInventoryParams, setMoveInventoryParams] = useState<MoveInventoryParams | null>(null);

  const onLaunchMoveInventoryModal = useCallback(
    (inventoryLocationId: string, lotUuid: string) => {
      setMoveInventoryParams({ inventoryLocationId, lotUuid });
      openMoveInventoryModal();
    },
    [openMoveInventoryModal],
  );

  const shouldDisableButton = !productSku || productId === 0 || !isActive;

  return (
    <>
      <FlexRow className={style['locations-container-flex']} gap={Gap.Space8}>
        <Text className={style['allocations-title']} element='div' textType={TextType.H4} value={messages.warehousesHeader} />
        <Button
          disabled={shouldDisableButton}
          disabledTooltipMessage={<FormattedMessage {...messages.locationsFooterLinkInactiveProduct} />}
          disabledTooltipMessagePosition='bottom'
          onClick={openAddInventoryModal}
          theme='secondary'
        >
          <FontAwesomeIcon icon={faPlus} /> <Text element='span' textType={TextType.Paragraph} value={messages.addStockButton} />
        </Button>
      </FlexRow>
      <div className={style['locations-container']}>
        <LocationsHeader />
        {isLoadingInventoryLocations ? <LoadingIndicator className={style['loading-indicator']} theme='grey' /> : null}
        {!isLoadingInventoryLocations &&
          (inventoryLocations as InventoryLocationItem[]).map((inv, index) => (
            <InventoryLocation
              key={index}
              allocated={inv.allocated}
              available={inv.available}
              expirationDate={inv.expirationDate ?? ''}
              inventoryLocationId={inv.inventoryLocationId}
              location={inv.location}
              lot={inv.lot}
              lotUuid={inv.lotUuid}
              onReloadLocations={onReloadLocations}
              openMoveInventoryModal={onLaunchMoveInventoryModal}
              productActive={isActive}
              productId={String(productId ?? '')}
              stock={inv.stock}
              warehouseName={inv.warehouseName}
            />
          ))}
        {!isLoadingInventoryLocations && inventoryLocations.length === 0 && <NoLocationsMade />}
      </div>
      {isAddInventoryModalOpen ? (
        <ModalPortal onClose={closeAddInventoryModal}>
          <AddStockModal
            inventoryWarehouseLocations={inventoryWarehouseLocations as WarehouseLocation[]}
            inventoryWarehouses={inventoryWarehouses as InventoryWarehouse[]}
            onClose={closeAddInventoryModal}
            onReloadLocations={onReloadLocations}
            productId={productId ?? 0}
            productName={productName}
            productSku={productSku}
            productWarehouseLocation={productWarehouseLocation}
          />
        </ModalPortal>
      ) : null}
      {isMoveInventoryModalOpen && moveInventoryParams ? (
        <ModalPortal onClose={closeMoveInventoryModal}>
          <MoveInventoryModal
            inventoryLocationId={moveInventoryParams.inventoryLocationId}
            inventoryWarehouseLocations={inventoryWarehouseLocations as WarehouseLocation[]}
            inventoryWarehouses={inventoryWarehouses as InventoryWarehouse[]}
            lotUuid={moveInventoryParams.lotUuid}
            onClose={closeMoveInventoryModal}
            onReloadLocations={onReloadLocations}
            productId={String(productId ?? '')}
            productName={productName}
            productSku={productSku}
          />
        </ModalPortal>
      ) : null}
    </>
  );
};
